import { App, ContainersPermission } from '@back4app2/sdk';

/**
 * Checks if user has Read permission for a feature
 */
export function hasReadPermission(
  app: App,
  feature: keyof ContainersPermission | string
): boolean {
  // Owner has all permissions
  if (app.isOwner) {
    return true;
  }

  // If no collaborator access, no permission
  if (!app.collabAccess) {
    return false;
  }

  // Pending collaborators don't have access
  if (app.collabAccess.isPending) {
    return false;
  }

  const permissions = app.collabAccess.containersPermission;

  // Check top-level permissions
  if (feature === 'Overview' || feature === 'Logs' || feature === 'Deployments' || feature === 'Metrics') {
    const permission = permissions[feature as keyof ContainersPermission] as string;
    return permission === 'Read' || permission === 'Write';
  }

  // Check Settings permissions
  if (feature === 'General') {
    return permissions.Settings.General === 'Read';
  }
  if (feature === 'BuildAndDeploy') {
    const permission = permissions.Settings.BuildAndDeploy;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Read' || permission === 'Write';
  }
  if (feature === 'EnvVars') {
    const permission = permissions.Settings.EnvVars;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Read' || permission === 'Write';
  }
  if (feature === 'Domain') {
    const permission = permissions.Settings.Domain;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Read' || permission === 'Write';
  }
  if (feature === 'HealthAndAlert') {
    const permission = permissions.Settings.HealthAndAlert;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Read' || permission === 'Write';
  }

  // Check Actions permissions
  if (feature === 'Deploy') {
    const permission = permissions.Actions.Deploy;
    // Actions.Deploy only supports 'Read' | 'Write', no 'None' option
    return permission === 'Read' || permission === 'Write';
  }

  return false;
}

/**
 * Checks if user has Write permission for a feature
 */
export function hasWritePermission(
  app: App,
  feature: keyof ContainersPermission | string
): boolean {
  // Owner has all permissions
  if (app.isOwner) {
    return true;
  }

  // If no collaborator access, no permission
  if (!app.collabAccess) {
    return false;
  }

  // Pending collaborators don't have access
  if (app.collabAccess.isPending) {
    return false;
  }

  const permissions = app.collabAccess.containersPermission;

  // Check top-level permissions (all are Read-only)
  if (feature === 'Overview' || feature === 'Logs' || feature === 'Deployments' || feature === 'Metrics') {
    return false; // These are always Read-only
  }

  // Check Settings permissions
  if (feature === 'General') {
    return false; // General is always Read-only
  }
  if (feature === 'BuildAndDeploy') {
    const permission = permissions.Settings.BuildAndDeploy;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Write';
  }
  if (feature === 'EnvVars') {
    const permission = permissions.Settings.EnvVars;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Write';
  }
  if (feature === 'Domain') {
    const permission = permissions.Settings.Domain;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Write';
  }
  if (feature === 'HealthAndAlert') {
    const permission = permissions.Settings.HealthAndAlert;
    // Explicitly check for 'None' and deny access
    if (permission === 'None') {
      return false;
    }
    return permission === 'Write';
  }

  // Check Actions permissions
  if (feature === 'Deploy') {
    const permission = permissions.Actions.Deploy;
    // Actions.Deploy only supports 'Read' | 'Write', no 'None' option
    return permission === 'Write';
  }

  return false;
}

